// ✅ Added missing imports at the very top
import React from "react";
import ReactDOM from "react-dom/client"; // Needed for ReactDOM.createRoot
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import usePageTracking from "./pages/usePageTracking";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Dashboard from "./pages/Dashboard";
import Players from "./pages/Players";
import Compare from "./pages/Compare";
import DreamTeam from "./components/DreamTeam";
import InjuryNews from "./components/InjuryNews";
import Suggestion from "./components/Suggestion";
import Footer from "./components/Footer";
import TieAnalyzer from "./components/TieAnalyzer";
import History from "./components/History";
import Live from "./components/Live";
import PriceChangePrediction from "./components/PriceChangePrediction";
import AdComponent from "./components/AdComponent";
import SquadCompare from "./components/SquadCompare";
import Versus from "./components/Versus";
import CheckRegistration from "./pages/CheckRegistration";
import SquadRival from "./pages/SquadRival";
import SquadRivalAdmin from "./pages/SquadRivalAdmin";
import SquadRivalTeam from "./pages/SquadRivalTeam";
import Gauntlet from "./pages/Gauntlet";
import GauntletAdmin from "./pages/GauntletAdmin";

import CaptainMode from "./pages/CaptainMode";
import Elimination from "./pages/Elimination";
import EliminationAdmin from "./pages/EliminationAdmin";
import FPLLive from "./pages/FPLLive";

import CleanSheetPrediction from "./pages/CleanSheetPrediction";

import { Navigate } from "react-router-dom";           // add Navigate to the existing react-router-dom import if you prefer
import { FplIdProvider, useFplId } from "./context/FplIdContext";
import Onboarding from "./pages/Onboarding";
import MyTeam from "./pages/MyTeam";
import LeagueSeasonHub from "./pages/LeagueSeasonHub";


const PageTrackingWrapper = () => {
  usePageTracking();
  return null;
};

const AppInner = () => {
  const { pathname } = useLocation();

  // Drawer (mobile) + Collapse (desktop)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("fpl_sidebar_collapsed") === "1");

  const toggleSidebar = () => setSidebarOpen((v) => !v);
  const toggleCollapse = () => setSidebarCollapsed((v) => {
    const next = !v;
    localStorage.setItem("fpl_sidebar_collapsed", next ? "1" : "0");
    return next;
  });
  const toggleMainMenu = () => {
    if (window.matchMedia("(min-width: 768px)").matches) toggleCollapse();
    else toggleSidebar();
  };

  // Close mobile drawer on route change
  useEffect(() => {
    setSidebarOpen(false);
    // Refresh Google Ads on navigation (if present)
    try {
      if (window?.adsbygoogle) window.adsbygoogle.push({});
    } catch (_) {}
  }, [pathname]);

  // World Cup pages: hide sidebar
  const hideSidebar = useMemo(() => pathname.startsWith("/worldcup"), [pathname]);

  // Left margin for main content on desktop
  const mainOffsetClass = useMemo(() => {
    if (hideSidebar) return "md:ml-0";
    return sidebarCollapsed ? "md:ml-20" : "md:ml-[236px]";
  }, [hideSidebar, sidebarCollapsed]);

  // Prevent body scroll when drawer is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => (document.body.style.overflow = "");
  }, [sidebarOpen]);

  // SquadRival is a standalone competition experience: no main-app sidebar, navbar, ads or footer.
  if (pathname.startsWith("/squad-rival")) {
    return (
      <>
        <PageTrackingWrapper />
        <Routes>
          <Route path="/squad-rival" element={<SquadRival />} />
          <Route path="/squad-rival/admin" element={<SquadRivalAdmin />} />
          <Route path="/squad-rival/team/:squadCode" element={<SquadRivalTeam />} />
        </Routes>
      </>
    );
  }

  // Gauntlet is a completely separate standalone competition experience.
  if (pathname.startsWith("/gauntlet")) {
    return (
      <>
        <PageTrackingWrapper />
        <Routes>
          <Route path="/gauntlet" element={<Gauntlet />} />
          <Route path="/gauntlet/admin" element={<GauntletAdmin />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <PageTrackingWrapper />

      <div className="core-app min-h-screen flex text-slate-900">
        {/* Sidebar */}
        {!hideSidebar && (
          <Sidebar
            isOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
            collapsed={sidebarCollapsed}
          />
        )}

        {/* Main column */}
        <div className={`flex-1 flex flex-col w-full transition-all duration-300 ${mainOffsetClass}`}>
          {/* Top Nav */}
          <Navbar toggleSidebar={toggleMainMenu} showMenu={!hideSidebar} />

          {/* Scrollable page content area */}
          <main className="core-main flex-1 px-3 sm:px-4 lg:px-5 py-4 sm:py-5 overflow-x-hidden overflow-y-auto">
            <Routes>
              {/* Main Pages */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/players" element={<Players />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/classic-league" element={<Navigate to="/s16?tab=standings" replace />} />
              <Route path="/dream-team" element={<DreamTeam />} />
              <Route path="/injuries" element={<InjuryNews />} />
              <Route path="/suggestion" element={<Suggestion />} />
              <Route path="/tieanalyzer" element={<TieAnalyzer />} />
              <Route path="/history" element={<History />} />
              <Route path="/live" element={<Live />} />
              <Route path="/price-change-prediction" element={<PriceChangePrediction />} />
              <Route path="/squad-compare" element={<SquadCompare />} />
              <Route path="/versus" element={<Versus />} />
              <Route path="/check-registration" element={<CheckRegistration />} />
              <Route path="/captainmode" element={<CaptainMode />} />
              <Route path="/gwwinner" element={<Navigate to="/s16?tab=gw" replace />} />
              <Route path="/league-stats" element={<Navigate to="/s16?tab=stats" replace />} />
              <Route path="/monthly-winner" element={<Navigate to="/s16?tab=month" replace />} />
              <Route path="/elimination" element={<Elimination />} />
              <Route path="/elimination-admin" element={<EliminationAdmin />} />
              <Route path="/fpllive" element={<FPLLive />} />
              {/* Squad Planner removed from the FPL Nepal webapp. Keep old URLs harmless. */}
              <Route path="/create-squad" element={<Navigate to="/" replace />} />
              <Route path="/tinker-squad/:id" element={<Navigate to="/" replace />} />
              <Route path="/my-squads" element={<Navigate to="/" replace />} />
              <Route path="/set-team" element={<Navigate to="/" replace />} />
              <Route path="/auth/callback" element={<Navigate to="/" replace />} />
              <Route path="/clean-sheet" element={<CleanSheetPrediction />} />
              <Route path="/my-team" element={<MyTeam />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/s16" element={<LeagueSeasonHub competition="s16" />} />
              <Route path="/the-100" element={<LeagueSeasonHub competition="the100" />} />

              <Route path="/login" element={<Navigate to="/" replace />} />

              {/* World Cup (no sidebar) */}
            </Routes>

            <div className="mt-6">
              <AdComponent />
            </div>
          </main>

          <Footer />
        </div>
      </div>
    </>
  );
};

// ✅ App and render (unchanged, just wrapped correctly with AuthProvider)
const Gate = () => {
  const { loading } = useFplId();
  const { pathname } = useLocation();
  const isStandaloneCompetition = pathname.startsWith("/squad-rival") || pathname.startsWith("/gauntlet");
  if (isStandaloneCompetition) return <AppInner />;
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0a0416] text-white">
        Loading…
      </div>
    );
  }

  // The app is public by default. Do not interrupt first-time visitors with
  // the FPL ID onboarding screen. Users can connect a team whenever they want
  // from the "Add a Team" button in the top-right navbar.
  return <AppInner />;
};

const App = () => (
  <Router>
    <FplIdProvider>
      <Gate />
    </FplIdProvider>
  </Router>
);

export default App;

